import { Outlet, Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLocale } from '@/contexts/LocaleContext';
import { Menu, X, Globe, ChevronDown, ArrowRight } from 'lucide-react';

const NAV_ITEMS = [
  { key: 'nav.programs', path: 'programs' },
  { key: 'nav.about', path: 'about' },
  { key: 'nav.admissions', path: 'admissions' },
  { key: 'nav.seminars', path: 'seminars' },
  { key: 'nav.magazine', path: 'magazine' },
  { key: 'nav.testimonials', path: 'testimonials' },
  { key: 'nav.contact', path: 'contact' },
];

export default function PublicLayout() {
  const { locale: urlLocale } = useParams();
  const { user, isAdmin, logout } = useAuth();
  const { locale, setLocale, t, locales, localeLabels } = useLocale();
  const [menuOpen, setMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [programs, setPrograms] = useState([]);
  const [catBlock, setCatBlock] = useState(null);
  const [contactInfo, setContactInfo] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const langRef = useRef(null);

  useEffect(() => {
    if (urlLocale && locales.includes(urlLocale) && urlLocale !== locale) {
      setLocale(urlLocale);
    }
  }, [urlLocale, locale, setLocale, locales]);

  useEffect(() => {
    const loc = locale || 'en';
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/programs?locale=${loc}`)
      .then(r => r.json())
      .then(d => setPrograms(d.programs || []))
      .catch(() => {});
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/site-content?locale=${loc}`)
      .then(r => r.json())
      .then(d => {
        setCatBlock(d.blocks?.program_categories || null);
        setContactInfo(d.blocks?.contact_info?.content || null);
      })
      .catch(() => {});
  }, [locale]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  // Close language dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) {
        setLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const switchLocale = (newLocale) => {
    setLocale(newLocale);
    const pathParts = location.pathname.split('/');
    pathParts[1] = newLocale;
    navigate(pathParts.join('/'));
    setLangOpen(false);
  };

  return (
    <div className="min-h-screen overflow-x-hidden" data-testid="public-layout">
      {/* Compact Mobile-First Navigation */}
      <nav
        data-testid="main-navigation"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? 'glass-strong shadow-sm' : 'bg-white/80 backdrop-blur-md'
        }`}
        style={{ height: '56px' }}
      >
        <div className="max-w-[1600px] mx-auto px-4 md:px-8 h-full flex items-center justify-between">
          {/* Logo */}
          <Link to={`/${locale}`} className="flex items-center gap-1 shrink-0" data-testid="logo-link">
            <span className="text-lg font-bold tracking-tight" style={{ fontFamily: 'Syne, sans-serif' }}>
              MISEL<span className="font-light ml-0.5">GROUP</span>
            </span>
          </Link>

          {/* Right side: CTA + Language + Menu */}
          <div className="flex items-center gap-2 md:gap-3">
            {/* Desktop CTA Button */}
            <Link
              to={`/${locale}/inquiry`}
              className="hidden md:inline-flex items-center justify-center rounded-full px-5 py-2.5 text-sm font-semibold tracking-wide text-white transition-all duration-300 hover:scale-[1.03] hover:shadow-lg"
              style={{ background: 'linear-gradient(135deg, #D4AF37 0%, #B8941F 100%)', fontFamily: 'Manrope, sans-serif' }}
              data-testid="header-cta-button"
            >
              {t('common.apply_now')}
              <ArrowRight size={14} className="ml-1.5" strokeWidth={2} />
            </Link>

            {/* Language Switcher - Compact */}
            <div className="relative" ref={langRef}>
              <button
                onClick={() => setLangOpen(!langOpen)}
                className="flex items-center gap-1 text-xs font-medium text-foreground/70 hover:text-foreground transition-colors px-2 py-1.5 rounded-full hover:bg-foreground/5"
                data-testid="language-switcher"
              >
                <Globe size={14} strokeWidth={1.5} />
                <span className="uppercase">{locale}</span>
                <ChevronDown size={12} strokeWidth={1.5} className={`transition-transform ${langOpen ? 'rotate-180' : ''}`} />
              </button>
              {langOpen && (
                <div className="absolute right-0 top-full mt-1 bg-white rounded-2xl shadow-xl border border-foreground/5 p-1.5 min-w-[160px] max-h-[260px] overflow-y-auto z-[100] animate-scale-in" data-testid="language-dropdown">
                  {locales.map(l => (
                    <button
                      key={l}
                      onClick={() => switchLocale(l)}
                      className={`w-full text-left px-3 py-1.5 rounded-xl text-sm transition-colors ${l === locale ? 'bg-foreground/5 font-medium' : 'hover:bg-foreground/5'}`}
                      data-testid={`locale-${l}`}
                    >
                      {localeLabels[l]}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Hamburger Menu Toggle */}
            <button
              className="p-2 rounded-full hover:bg-foreground/5 transition-colors"
              onClick={() => setMenuOpen(!menuOpen)}
              data-testid="mobile-menu-toggle"
              aria-label="Menu"
            >
              {menuOpen ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Full-Screen Menu Overlay */}
      {menuOpen && (
        <div className="fixed inset-0 z-40 bg-white flex flex-col animate-fade-in" data-testid="mobile-menu" style={{ paddingTop: '56px' }}>
          <div className="flex-1 overflow-y-auto px-6 py-8">
            <div className="flex flex-col gap-1">
              {NAV_ITEMS.map(item => (
                <Link
                  key={item.key}
                  to={`/${locale}/${item.path}`}
                  className="text-2xl font-semibold tracking-tight text-foreground hover:text-gold transition-colors py-2"
                  style={{ fontFamily: 'Syne, sans-serif' }}
                  data-testid={`mobile-nav-${item.path}`}
                >
                  {t(item.key)}
                </Link>
              ))}
            </div>

            <div className="w-12 h-px bg-foreground/10 my-6" />

            {user ? (
              <div className="flex flex-col gap-2">
                <Link to="/portal/dashboard" className="text-base text-foreground/70 font-medium py-1" data-testid="menu-portal-link">{t('nav.portal')}</Link>
                {isAdmin && <Link to="/admin/dashboard" className="text-base text-foreground/70 font-medium py-1" data-testid="menu-admin-link">{t('nav.admin')}</Link>}
                <button onClick={() => { logout(); setMenuOpen(false); }} className="text-base text-foreground/50 font-medium py-1 text-left" data-testid="menu-logout">{t('nav.logout')}</button>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                <Link to="/auth/login" className="text-base text-foreground/70 font-medium py-1" data-testid="menu-login">{t('nav.login')}</Link>
                <Link to={`/${locale}/admissions`} className="btn-primary mt-2 w-full justify-center" data-testid="menu-apply">
                  {t('nav.apply')} <ArrowRight size={16} className="ml-2" strokeWidth={1.5} />
                </Link>
              </div>
            )}

            {/* Language Grid at Bottom */}
            <div className="mt-8 pt-6 border-t border-foreground/5">
              <p className="text-xs uppercase tracking-widest text-foreground/30 mb-3">{t('common.language')}</p>
              <div className="flex flex-wrap gap-2">
                {locales.map(l => (
                  <button
                    key={l}
                    onClick={() => { switchLocale(l); setMenuOpen(false); }}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${l === locale ? 'bg-foreground text-background' : 'bg-foreground/5 text-foreground/60 hover:bg-foreground/10'}`}
                    data-testid={`menu-locale-${l}`}
                  >
                    {localeLabels[l]}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Page Content - with top padding for fixed nav */}
      <main>
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-foreground text-white" data-testid="main-footer">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 md:gap-8">
            <div className="md:col-span-2">
              <h3 className="text-2xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
                MISEL<span className="font-light ml-1">GROUP</span>
              </h3>
              <p className="text-white/50 text-lg leading-relaxed max-w-md" style={{ fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic' }}>
                {t('footer.tagline')}
              </p>
            </div>
            <div>
              <h4 className="text-xs uppercase tracking-[0.2em] text-white/40 mb-4">{t('footer.programs')}</h4>
              <div className="flex flex-col gap-2">
                {(() => {
                  const types = [...new Set(programs.map(p => p.program_type).filter(Boolean))];
                  // Use CMS labels if available, else fallback
                  const cmsContent = catBlock?.content || {};
                  const getLabel = (type) => {
                    if (cmsContent[type]?.title) return cmsContent[type].title;
                    return type.charAt(0).toUpperCase() + type.slice(1);
                  };
                  // If CMS has an order, use it; else use order from programs
                  const order = catBlock?.meta?.order || [];
                  const displayTypes = order.length > 0
                    ? order.filter(t => types.includes(t))
                    : types;
                  return displayTypes.map(type => (
                    <Link key={type} to={`/${locale}/programs?type=${type}`} className="text-sm text-white/60 hover:text-white transition-colors">
                      {getLabel(type)}
                    </Link>
                  ));
                })()}
              </div>
            </div>
            <div>
              <h4 className="text-xs uppercase tracking-[0.2em] text-white/40 mb-4">{t('footer.company')}</h4>
              <div className="flex flex-col gap-2">
                <Link to={`/${locale}/about`} className="text-sm text-white/60 hover:text-white transition-colors">{t('nav.about')}</Link>
                <Link to={`/${locale}/admissions`} className="text-sm text-white/60 hover:text-white transition-colors">{t('nav.admissions')}</Link>
                <Link to={`/${locale}/magazine`} className="text-sm text-white/60 hover:text-white transition-colors">{t('nav.magazine')}</Link>
                <Link to={`/${locale}/contact`} className="text-sm text-white/60 hover:text-white transition-colors">{t('nav.contact')}</Link>
                <Link to={`/${locale}/faq`} className="text-sm text-white/60 hover:text-white transition-colors">{t('nav.faq')}</Link>
              </div>
            </div>
          </div>
          {/* Contact info in footer */}
          {contactInfo && (
            <div className="mt-12 pt-8 border-t border-white/10 grid grid-cols-1 md:grid-cols-3 gap-6 text-sm text-white/50">
              {contactInfo.address && (
                <div>
                  <p>{contactInfo.address}</p>
                  {contactInfo.city && <p>{contactInfo.city}</p>}
                </div>
              )}
              {contactInfo.phone && (
                <div>
                  <a href={`tel:${contactInfo.phone.replace(/\s/g, '')}`} className="hover:text-white/80 transition-colors">{contactInfo.phone}</a>
                </div>
              )}
              {contactInfo.email && (
                <div>
                  <a href={`mailto:${contactInfo.email}`} className="hover:text-white/80 transition-colors">{contactInfo.email}</a>
                </div>
              )}
            </div>
          )}
          <div className="mt-8 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-xs text-white/30">&copy; {new Date().getFullYear()} MISEL GROUP MASTERCLASS. {t('footer.rights')}</p>
            <div className="flex gap-6">
              {contactInfo?.digital_url && (
                <a href={contactInfo.digital_url} target="_blank" rel="noopener noreferrer" className="text-xs text-white/30 hover:text-white/60 transition-colors">{t('footer.online_education')}</a>
              )}
              {contactInfo?.shop_url && (
                <a href={contactInfo.shop_url} target="_blank" rel="noopener noreferrer" className="text-xs text-white/30 hover:text-white/60 transition-colors">{t('footer.shop')}</a>
              )}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
